 
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, combineLatest, first, switchMap } from "rxjs";
import { Program } from "src/app/services/api";
import { RootState } from "src/app/store";
import * as FromUser from "src/app/store/user/selectors";
import * as FromApp from "src/app/store/app/selectors";
import { AppRoutes } from "src/app/enums";
import * as Sentry from "@sentry/capacitor";

@Injectable({ providedIn: "root" })
export class AppInterceptor implements HttpInterceptor {
  private readonly store = inject<Store<RootState>>(Store);

  private readonly userId$: Observable<string> = this.store.select(FromUser.selectUserId);
  private readonly program$: Observable<Program> = this.store.select(FromApp.selectProgram);
  constructor() {}

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return combineLatest([this.userId$, this.program$]).pipe(
      first(),
      switchMap(([userId, program]) => {
        req = req.clone({ setHeaders: { "X-APP": "customer" } });
        req = req.clone({ setHeaders: { "X-PATHNAME": this.getRouteConfigPath() } });
        req = req.clone({ setHeaders: { "X-HREF": window.location.href } });
        req = req.clone({ setHeaders: { "X-APP-TIMEZONE": Intl.DateTimeFormat().resolvedOptions().timeZone } });

        if (userId) req = req.clone({ setHeaders: { "X-USER-ID": userId } });
        if (program?.id) req = req.clone({ setHeaders: { "X-PROGRAM-ID": program?.id } });
        if (program?.slug) req = req.clone({ setHeaders: { "X-PROGRAM-SLUG": program?.slug } });

        return next.handle(req);
      }),
    );
  }

  private getRouteConfigPath(): string {
    try {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const redirectTo = urlSearchParams.get("redirectTo");

      if (redirectTo?.startsWith("%%")) return redirectTo;

      const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?/;
      if (urlRegex.test(redirectTo)) return decodeURIComponent(redirectTo);

      const validRouteValues = Object.values(AppRoutes) as string[];
      const path = redirectTo ? decodeURIComponent(redirectTo) : window.location.pathname;
      const segments = path.split("/").filter(Boolean);

      if (!redirectTo) segments.shift();

      return segments
        .map(segment => validRouteValues.includes(segment) ? segment : ":param")
        .join("/");
    } catch (error) {
      Sentry.captureException(error);
      return "";
    }
  }
}
